import { computed, toValue } from 'vue';
import usePageDataStore from '~/stores/usePageData';
import { useBrand } from '~/composables/useBrand';
import { usePageData } from '~/composables/usePageData';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { numOfAttributes, getUrlVars } from '~/utils/commonUtils';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useType } from '~/modules/type/composables/useType';
import useSize from '~/modules/size/composables/useSize';
import useColor from '~/modules/color/composables/useColor';
import { useDivisionCategoryFlags } from '~/modules/featureControl/composables/useDivisionCategoryFlags';
import useImages from '~/composables/images/useImages';
import { useWnm } from '~/modules/worry-no-more/composables/useWnm';
import useExperiments from '~/stores/useExperimentsStore';
import useReviews from '~/modules/reviews/composables/useReviews';
import { useProductType } from '~/composables/useProductType';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import { getMemberProduct } from '~/utils/product';
import type { Color, MemberProductMapProduct } from '~~/shared/globalTypes/product';
import type { Killswitches } from '~~/server/pageData/featureControl/mappers/killswitches';
import getAnalyticsProductTypeData from '~/utils/analytics';
import { fireViewTag as fireTag } from './tagManager';

interface UtagData {
  page_type?: string;
  page_name?: string;
  error_message?: string;
  t_category_id?: string[];
  t_category_name?: string[];
  is_big_ticket?: boolean[];
  is_big_ticket_v2c?: boolean[];
  product_id?: string[];
  product_low_availability?: boolean[];
  product_availability_message?: string[];
  product_name?: string[];
  product_type?: string[];
  product_original_price?: string[];
  product_price?: string[];
  product_pricing_state?: (string | undefined)[];
  product_rating?: string[];
  product_reviews?: string[];
  product_video?: string[] | boolean[];
  product_gwp_available?: string[];
  product_pwp_available?: string[];
  true_fit_eligible?: boolean[];
  findation_eligible?: boolean[];
  product_brand?: string[];
  consolidated_product?: string;
  master_pdp_type?: string;
  gdp_eligible?: boolean;
  experimentation_ids?: string[];
  product_promo_code_flag?: string[];
  pdp_outfitting?: string[];
  pdp_outfitting_type?: string;
  is_responsive_pdp?: string;
  eligible_attribute_count?: string;
  ccfp_eligible?: boolean;
  topLevelCategoryId?: string;
  topLevelCategoryName?: string;
  prosContent?: string | null;
  recommendation_milanid?: string;
  reommendation_model?: string;
  recommendation_position?: string;
  recommendation_title?: string;
  recommendation_zone?: string;
  notification_msg?: string;
  pdp_variant_type?: number;
  product_written_reviews?: string[];
  product_numofimages?: number;
  product_numofvideos?: number;
  marketPlaceItem?: boolean;
  marketPlaceSpecialHandling?: string[];
  product_promo_messaging?: string[];
  experiment_key?: string;
  experiment_key_type?: string;
  pdp_highlights_eligible?: string[];
  junk_fee_display?: boolean;
  video_id?: string[];
  pdp_product_id?: string;
  toplevel_category_id?: string;
  product_badge_new_arrival?: string;
  product_badge_new_markdown?: string[];
  protection_plan_eligible?: string[];
  protection_plan_purchased?: string[];
  protection_plan_selected?: string[];
}

interface FireViewTagOptions {
  writeReviewPage?: boolean;
}

function getAvailabilityMessage(product: Product): string[] {
  const availability = product.availability;
  if (!availability) {
    return ['this product is currently unavailable'];
  }

  if (!availability.available) {
    return ['this product is currently unavailable'];
  }

  return availability.lowAvailability
    ? [`just a few left: shipped by marketplace in ${availability.shipDays} days`]
    : [`Shipped by marketplace in ${availability.shipDays} days`];
}

/**
 * gets product data for analytics
 */
function getProductData(product: Product) {
  const { topLevelCategoryID, isCcfpAvailable, topLevelCategoryName, productId } = useCommonProductGetters();

  return {
    topLevelCategoryID: toValue(topLevelCategoryID),
    ccfpEligible: toValue(isCcfpAvailable),
    topLevelCategoryName: toValue(topLevelCategoryName),
    marketPlaceSpecialHandling: product.detail?.selectedUpcItem?.splHandlingAttrbs,
    pricingText: product.pricing?.price?.priceType?.text,
    registryData: product.registryData,
    imagesCount: product.imagery?.images?.length ?? 0,
    pdpProductId: toValue(productId),
  };
}

/**
 * Determines how many interactive variants we display to customers.
 * Interactive means type/size chips or color swatches are displayed with multiple clickable options.
 */
function getPdpVariantType(): number {
  try {
    const { types } = useType();
    const { sizes } = useSize();
    const { colors } = useColor();
    const { isMattressesDivision } = useDivisionCategoryFlags();

    const typesCount = types.value?.length ?? 0;
    const sizesCount = sizes.value?.length ?? 0;
    const colorsCount = isMattressesDivision ? 0 : (colors.value?.length ?? 0);

    const variants = [typesCount, sizesCount, colorsCount];

    // if a product has more than 1 type/size/color, type/size chips & color swatches will render
    return variants.filter((variant) => variant > 1).length;
  } catch {
    return 0;
  }
}

/**
 * Returns the review range in which the number of written reviews falls into.
 * @returns ['0-24'], ['25-49'], ['75-99'], ['200-299'], ['3000-3999'], ['5000+']...
 */
function getWrittenReviewsRange(): string[] {
  try {
    const { totalResults } = useReviews();
    const writtenReviewsCount = totalResults?.value || 0;

    if (writtenReviewsCount <= 0) {
      return ['0-24'];
    }

    let reviewsRange: string;

    if (writtenReviewsCount <= 99) {
      const rangeFrom = Math.floor(writtenReviewsCount / 25) * 25;
      const rangeTo = rangeFrom + 24;
      reviewsRange = `${rangeFrom}-${rangeTo}`;
    } else if (writtenReviewsCount >= 5000) {
      reviewsRange = '5000+';
    } else {
      const firstDigit = parseInt(writtenReviewsCount.toString()[0], 10);
      const isHundreds = writtenReviewsCount < 1000;
      const base = isHundreds ? 100 : 1000;
      const rangeFrom = firstDigit * base;
      const rangeTo = rangeFrom + base - 1;
      reviewsRange = `${rangeFrom}-${rangeTo}`;
    }

    return [reviewsRange];
  } catch {
    return ['0-24'];
  }
}

/**
 * Returns product rating rounded to the first decimal.
 * @param rating - ['4.85'], ['3.847'], ['4.4']
 * @returns ['4.9'], ['3.8'], ['4.4']
 */
function roundRatingToFirstDecimal(rating?: string[]): string[] {
  if (!rating || rating.length === 0) {
    return [];
  }

  return rating.map((r) => {
    const parsed = parseFloat(r);
    return Number.isNaN(parsed) ? r : parsed.toFixed(1);
  });
}

function getPromoMessaging(): string[] {
  const selectedPrice = useProductPrice();
  const offer = selectedPrice.value?.badgesList?.[0];

  return offer?.promoId ? [offer.promoId] : ['no promo messaging'];
}

function applyProtectionPlanUtagFields(pageUtagData: UtagData, productData: Product | null | undefined, isMcom: boolean): void {
  if (!productData) {
    return;
  }
  const { isProtectionPlanEligibleFlag } = useProductFlags();
  const { experimentFlags } = useExperiments();
  if (!isProtectionPlanEligibleFlag) {
    return;
  }
  if (!isMcom && !experimentFlags.isWorryNoMoreEnabled) {
    return;
  }
  pageUtagData.protection_plan_eligible = ['true'];
  try {
    const { selectedPlan } = useWnm();
    if (selectedPlan.value) {
      pageUtagData.protection_plan_purchased = [selectedPlan.value.name];
      pageUtagData.protection_plan_selected = ['true'];
    }
  } catch {
    // if no wnm provider is available
  }
}

function getDataLayerMcom(product: Product, data: UtagData, ks: Killswitches): UtagData {
  const { topLevelCategoryID, ccfpEligible, topLevelCategoryName, marketPlaceSpecialHandling, registryData, imagesCount } = getProductData(product);

  if (registryData?.event_name === 'registry add') {
    return data;
  }

  const { productData } = useProduct();
  const price = useProductPrice();
  const { isMarketPlaceEligibleFlag, isMasterProductFlag, homeFOBFlag, isBestSellerFlag, hasSizesFlag, hasTypesFlag, hasColorSwatchesFlag } =
    useProductFlags();

  const pdpOutfittingCap = computed(() => (!isMasterProductFlag && !homeFOBFlag ? ['true'] : ['false']));
  const videoEnabled = computed(() => data.product_video?.[0] === 'true');
  const { buyNow } = useFeatureEligibility();

  data.product_promo_code_flag = price.value.flags.isWithOfferPromo ? ['true'] : ['false'];
  if (isMarketPlaceEligibleFlag) {
    data.product_availability_message = getAvailabilityMessage(product);
  }
  data.pdp_outfitting = pdpOutfittingCap.value;
  if (pdpOutfittingCap.value) {
    data.pdp_outfitting_type = 'wear it with';
  }
  data.is_responsive_pdp = 'true';
  data.eligible_attribute_count = numOfAttributes(productData, hasColorSwatchesFlag, hasSizesFlag, hasTypesFlag);
  data.ccfp_eligible = ccfpEligible;
  data.topLevelCategoryId = topLevelCategoryID;
  data.topLevelCategoryName = topLevelCategoryName;
  const params = getUrlVars(window.location.href) as Record<string, string>;
  data.prosContent = params.prosContent || null;
  data.recommendation_milanid = params.RecommendationMilanid || params.prosMilanId || 'no milanid';
  data.reommendation_model = params.RecommendationModel || params.prosModelId || '';
  data.recommendation_position = params.RecommendationPosition || params.prosPosition || '';
  data.recommendation_title = params.RecommendationTitle || params.prosTitle || '';
  data.recommendation_zone = params.RecommendationZone || params.prosZone || '';
  data.notification_msg = 'no pill';
  data.pdp_variant_type = getPdpVariantType();
  data.product_written_reviews = getWrittenReviewsRange();
  // product_rating is an existing xAPI analytics field, we just format it on UI
  data.product_rating = roundRatingToFirstDecimal(data.product_rating);
  if (ks.isPdpProductEngagementPromptTrtTwoEnabled) {
    data.notification_msg = isBestSellerFlag ? 'best seller (pill)' : 'customers purchased (pill)';
  }

  data.product_numofimages = imagesCount;
  data.product_numofvideos = videoEnabled.value ? 1 : 0;
  data.marketPlaceItem = isMarketPlaceEligibleFlag;
  data.marketPlaceSpecialHandling = marketPlaceSpecialHandling;
  data.product_promo_messaging = getPromoMessaging();

  // experiments
  if (buyNow) {
    data.experiment_key = 'pdpCondensedCheckout:1076-21:pdp buy now impression:06142023:07302023';
    data.experiment_key_type = 'condition';
  }

  return {
    ...data,
  };
}

function getDataLayerBcom(product: Product, data: UtagData): UtagData {
  const { isMarketPlaceEligibleFlag, isNewFlag, isMasterProductFlag, homeFOBFlag } = useProductFlags();
  const { pdpProductId, ccfpEligible, topLevelCategoryID, pricingText } = getProductData(product);

  data.pdp_product_id = pdpProductId;
  data.ccfp_eligible = ccfpEligible;
  data.toplevel_category_id = topLevelCategoryID;
  data.marketPlaceItem = isMarketPlaceEligibleFlag;
  data.product_promo_messaging = getPromoMessaging();

  const NEW_MARKDOWN = 'new markdown';
  data.product_badge_new_arrival = isNewFlag ? 'new arrival' : '';
  data.product_badge_new_markdown = pricingText && pricingText.indexOf(NEW_MARKDOWN) > -1 ? [NEW_MARKDOWN] : [''];

  if (isMarketPlaceEligibleFlag) {
    data.product_availability_message = getAvailabilityMessage(product);
  }
  const pdpOutfittingCap = computed(() => (!isMasterProductFlag && !homeFOBFlag ? ['true'] : ['false']));
  data.pdp_outfitting = pdpOutfittingCap.value;
  if (pdpOutfittingCap.value) {
    data.pdp_outfitting_type = 'wear it with';
  }

  return {
    ...data,
  };
}

/**
 * Checks if product has multiple price types across colors.
 */
function hasMultiplePriceType(colorMap: Record<number, Color> = {}): boolean {
  try {
    const priceTypeIds = Object.values(colorMap).map((colorData) => colorData?.pricing?.price?.priceTypeId);
    return new Set(priceTypeIds).size > 1;
  } catch {
    return false;
  }
}

/**
 * builds page utag data based on product availability and brand
 */
function buildPageUtagData(
  productData: Product | null,
  utagData: UtagData,
  isMcom: boolean,
  writeReviewPage: boolean | undefined,
  killswitches: Killswitches,
): UtagData {
  if (!productData) {
    if (isMcom) {
      return {
        ...utagData,
        page_type: 'product_unavailable',
        error_message: 'Sorry, this product is no longer available for purchase',
      };
    }
    return {
      ...utagData,
      page_type: 'error',
      page_name: 'product_unavailable',
    };
  }

  if (writeReviewPage) {
    utagData.page_type = 'responsive-review-page';
  }

  try {
    const mappedData = isMcom ? getDataLayerMcom(productData, utagData, killswitches) : getDataLayerBcom(productData, utagData);
    const productTypeObj = useProductType();

    const typeData = getAnalyticsProductTypeData(productTypeObj);
    Object.assign(mappedData, typeData);

    return mappedData;
  } catch {
    return { ...utagData };
  }
}

/**
 * fires the view analytics tag with product data
 */
export default function fireViewTag(options: FireViewTagOptions = {}): void {
  const { writeReviewPage } = options;

  try {
    const { killswitches } = useKillswitches();
    const { productData } = useProduct();
    const pageData = usePageData();
    const { isMcom } = useBrand();
    const { videoId } = useImages();

    const utagData: UtagData = { ...(pageData.product?.analytics?.data || {}) };
    utagData.experimentation_ids = pageData.experimentIds;

    const pageUtagData = buildPageUtagData(productData, utagData, isMcom, writeReviewPage, killswitches);

    // additional fee display
    if (productData?.additionalFee) {
      pageUtagData.junk_fee_display = true;
    }

    // multiple price type handling
    const colorMap = productData?.traits?.colors?.colorMap;
    const isMultiplePriceType = hasMultiplePriceType(colorMap);

    const pricingState = utagData?.product_pricing_state?.[0];
    pageUtagData.product_pricing_state = isMultiplePriceType ? ['mixed'] : [pricingState];

    // video data
    if (videoId.value) {
      pageUtagData.product_video = ['true'];
      pageUtagData.video_id = [`${videoId.value}`];
    }

    applyProtectionPlanUtagFields(pageUtagData, productData, isMcom);

    // PDP Highlights Flag implementation
    if (productData) {
      const { isConsolidatedPage } = useProductType();
      const { isShopTheSetExpEnabled } = useFeatureEligibility();
      const isConsolidatedOrShopTheSet = isConsolidatedPage || isShopTheSetExpEnabled;
      let selectedProduct: MemberProductMapProduct | Product | undefined;

      if (isConsolidatedOrShopTheSet) {
        const consolidatedStore = useConsolidatedMemberStore();
        const selectedMember = toValue(consolidatedStore?.selectedMember);
        selectedProduct =
          toValue(consolidatedStore?.memberProduct) ||
          (selectedMember ? getMemberProduct(productData, String(selectedMember)) : undefined) ||
          (productData?.relationships?.selectedProduct ? getMemberProduct(productData, productData?.relationships?.selectedProduct) : undefined);
      }

      const targetProduct = isConsolidatedOrShopTheSet && selectedProduct ? selectedProduct : productData;
      const isProductHighlights = !!targetProduct.detail?.flags?.isProductHighlights;
      //Flag to indicate highlights data is on a product
      const hasHighlightsData = !!targetProduct.detail?.flags?.hasHighlightsData;
      const experimentsStore = useExperiments();
      const isControl = experimentsStore.experimentIds?.has('2908-20');
      const isTreatment = experimentsStore.experimentIds?.has('2908-21');
      const experimentIsRunning = isTreatment || isControl;

      pageUtagData.pdp_highlights_eligible = hasHighlightsData ? ['true'] : ['false'];

      if (experimentIsRunning) {
        const expId = isTreatment ? '2908-21' : '2908-20';
        pageUtagData.experiment_key = isProductHighlights ? `pdpHighlights:${expId}:highlights shown` : `pdpHighlights:${expId}:highlights not shown`;
        pageUtagData.experiment_key_type = 'action';
      }
    }

    // experiment auto tagging
    const isAutoTaggingEnabled = pageData.product?.killswitches?.isAutoTaggingEnabled && pageData.experimentIds?.includes('1220-23');

    if (!isAutoTaggingEnabled) {
      fireTag(pageUtagData);
    }

    usePageDataStore().pageUtagData = pageUtagData;
  } catch {
    console.error('Error firing view tag');
  }
}
